import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router';
import { isSupportedBrowser } from 'vite-plugin-supported-browserslist/web';
import { isAxiosError } from 'axios';

const handleBeforeSend = (event: Sentry.ErrorEvent, hint?: Sentry.EventHint) => {
  // Ignore Axios errors (they are captured by the httpClientIntegration).
  if (isAxiosError(hint?.originalException)) {
    return null;
  }

  // Process all other events normally
  return event;
};

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,

  transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

  environment: import.meta.env.VITE_APP_ENV ?? 'local',

  release: import.meta.env.VITE_COMMIT_HASH_COMPLETE,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  normalizeDepth: 5,
  maxValueLength: 2500,

  integrations: [
    Sentry.extraErrorDataIntegration(),
    Sentry.eventFiltersIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.browserSessionIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.consoleLoggingIntegration({
      levels: ['log', 'warn', 'error']
    }),
    Sentry.replayIntegration({
      networkDetailAllowUrls: [/^https?:\/\/(localhost|.*\.atlasone\.co)\/api\//]
    })
  ],

  ignoreErrors: [
    'CanceledError',
    'Request aborted',
    'Access token expired',
    'Event `Event` (type=error) captured as promise rejection',
    'Invalid time value'
  ],

  beforeSend: handleBeforeSend,

  // Enable logs to be sent to Sentry
  enableLogs: true,

  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.0'),

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [import.meta.env.VITE_APP_DOMAIN ?? 'localhost', /\.atlasone\.co\/api\//],

  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
  replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? '0.05'),
  replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE ?? '1.0'),

  // 100% of sampled sessions will have a profile attached to it. The profile sample rate is applied *after* the trace sample rate.
  profileSessionSampleRate: 1,
  profileLifecycle: 'trace'
});

const isSupported = isSupportedBrowser();

Sentry.setTag('supported_browser', isSupported);
